import React, { createContext, useContext, useReducer } from 'react';

const CartContext = createContext();

// Clé pour le localStorage
const CART_STORAGE_KEY = 'la-ferme-a-lancienne-cart';

// Fonction pour charger le panier depuis localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      console.log('📦 Panier chargé depuis localStorage:', parsed.items?.length || 0, 'articles');
      return parsed;
    }
  } catch (error) {
    console.error('❌ Erreur lors du chargement du panier depuis localStorage:', error);
  }
  return { items: [] };
};

// Fonction pour sauvegarder le panier dans localStorage
const saveCartToStorage = (cartState) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartState));
    console.log('💾 Panier sauvegardé dans localStorage:', cartState.items?.length || 0, 'articles');
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde du panier dans localStorage:', error);
  }
};

const cartReducer = (state, action) => {
  let newState;
  
  switch (action.type) {
    case 'ADD_TO_CART':
      const product = action.payload.product || action.payload;
      const quantity = action.payload.quantity || 1;
      
      console.log('🛒 ADD_TO_CART:', {
        productId: product.id,
        productName: product.name,
        quantity: quantity,
        stock: product.stock,
        hasStripeIds: !!(product.stripeProductId && product.stripePriceId)
      });
      
      if (!product || !product.id) {
        console.error('❌ Produit invalide ajouté au panier:', product);
        return state;
      }
      
      // Vérifier si le produit est en rupture de stock
      if (product.stock === 0 || product.stock < quantity) {
        console.warn('⚠️ Produit en rupture de stock, ajout impossible:', product.name);
        return state;
      }
      
      const existingItem = state.items.find(item => item.id === product.id);
      if (existingItem) {
        // Vérifier que la nouvelle quantité totale ne dépasse pas le stock disponible
        const newTotalQuantity = existingItem.quantity + quantity;
        if (newTotalQuantity > product.stock) {
          console.warn('⚠️ Quantité demandée dépasse le stock disponible:', {
            stock: product.stock,
            existingQuantity: existingItem.quantity,
            requestedQuantity: quantity,
            total: newTotalQuantity
          });
          return state;
        }
        
        console.log(`➕ Produit existant, augmentation quantité: ${existingItem.quantity} → ${newTotalQuantity}`);
        newState = {
          ...state,
          items: state.items.map(item =>
            item.id === product.id
              ? { ...item, quantity: newTotalQuantity }
              : item
          )
        };
      } else {
        // Créer un nouvel item avec toutes les propriétés du produit
        const newItem = {
          ...product,
          quantity: quantity
        };
        
        console.log(`✨ Nouveau produit ajouté au panier:`, {
          id: newItem.id,
          name: newItem.name,
          quantity: newItem.quantity,
          price: newItem.price,
          stripeProductId: newItem.stripeProductId || 'Non défini',
          stripePriceId: newItem.stripePriceId || 'Non défini'
        });
        
        newState = {
          ...state,
          items: [...state.items, newItem]
        };
      }
      break;
    
    case 'REMOVE_FROM_CART':
      newState = {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
      break;
    
    case 'UPDATE_QUANTITY':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
      break;
    
    case 'CLEAR_CART':
      newState = {
        ...state,
        items: []
      };
      break;
    
    default:
      return state;
  }
  
  // Sauvegarder le panier dans localStorage après chaque modification
  saveCartToStorage(newState);
  
  return newState;
};

export const CartProvider = ({ children }) => {
  // Initialiser l'état depuis localStorage
  const [state, dispatch] = useReducer(cartReducer, loadCartFromStorage());

  const addToCart = (product, quantity = 1) => {
    console.log('🛒 addToCart appelé:', {
      productId: product?.id,
      productName: product?.name,
      quantity: quantity,
      stock: product?.stock,
      productKeys: product ? Object.keys(product) : []
    });
    
    // Validation du produit
    if (!product) {
      console.error('❌ Erreur: produit est null ou undefined');
      return;
    }
    
    if (!product.id) {
      console.error('❌ Erreur: produit sans ID:', product);
      return;
    }
    
    // Vérifier si le produit est en rupture de stock
    const productStock = product.stock || 0;
    if (productStock === 0) {
      console.warn('⚠️ Produit en rupture de stock, ajout impossible:', product.name);
      return;
    }
    
    // Vérifier que le produit a toutes les propriétés nécessaires
    const productToAdd = {
      id: product.id,
      name: product.name || 'Produit sans nom',
      price: product.price || 0,
      category: product.category || 'Autre',
      unit: product.unit || 'unité',
      image: product.image || '/images/beef-steak.svg',
      description: product.description || '',
      stock: productStock,
      featured: product.featured || false,
      // Inclure les IDs Stripe si disponibles
      stripeProductId: product.stripeProductId,
      stripePriceId: product.stripePriceId
    };
    
    dispatch({ 
      type: 'ADD_TO_CART', 
      payload: {
        product: productToAdd,
        quantity: quantity
      }
    });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    console.log('🗑️ Panier vidé');
    dispatch({ type: 'CLEAR_CART' });
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items: state.items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalPrice,
      getTotalItems
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};